import { useEffect } from 'react';

export interface PrototypeVariant {
  id: string;
  name: string;
}

interface PrototypeSwitcherProps {
  variants: readonly PrototypeVariant[];
  current: string;
  onSelect: (variantId: string) => void;
}

export function PrototypeSwitcher({
  variants,
  current,
  onSelect,
}: PrototypeSwitcherProps): JSX.Element {
  const currentIndex = variants.findIndex((v) => v.id === current);
  const activeIndex = currentIndex >= 0 ? currentIndex : 0;
  const activeVariant = variants[activeIndex] ?? variants[0];

  const goPrev = () => {
    const nextIndex = (activeIndex - 1 + variants.length) % variants.length;
    onSelect(variants[nextIndex]?.id ?? 'A');
  };

  const goNext = () => {
    const nextIndex = (activeIndex + 1) % variants.length;
    onSelect(variants[nextIndex]?.id ?? 'A');
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.isContentEditable)
      ) {
        return;
      }
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        goPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        goNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeIndex, variants]);

  return (
    <div
      role="region"
      aria-label="UI Prototype Variant Switcher"
      style={{
        position: 'fixed',
        bottom: 24,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 9999,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '6px 14px',
        background: 'var(--bg-panel)',
        color: 'var(--text)',
        backdropFilter: 'blur(8px)',
        border: '1px solid var(--border)',
        borderRadius: 9999,
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
        fontSize: 13,
        fontWeight: 500,
        userSelect: 'none',
      }}
    >
      <button
        type="button"
        onClick={goPrev}
        title="Previous variant (Left arrow)"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '4px 6px',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ◀
      </button>

      <span style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 180, justifyContent: 'center' }}>
        <span
          style={{
            background: '#3b82f6',
            color: '#ffffff',
            borderRadius: 4,
            padding: '2px 6px',
            fontSize: 11,
            fontWeight: 700,
          }}
        >
          {activeVariant?.id}
        </span>
        <span style={{ color: 'var(--text)' }}>{activeVariant?.name}</span>
      </span>

      <button
        type="button"
        onClick={goNext}
        title="Next variant (Right arrow)"
        style={{
          background: 'none',
          border: 'none',
          color: 'var(--text-muted)',
          cursor: 'pointer',
          padding: '4px 6px',
          borderRadius: 4,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        ▶
      </button>
    </div>
  );
}
